/**
 * This randomizer is meant to work the same way as the one from .Net Framework
 * https://referencesource.microsoft.com/#mscorlib/system/random.cs
 */

// Int32 bounds
const MAX_VALUE = 2 ** 31 - 1;
const MIN_VALUE = 2 ** 31 * -1;

interface Next {
    minValue?: number;
    maxValue?: number;
}

export function getRandomInt() {
    return Math.floor(Math.random() * Math.floor(MAX_VALUE));
}

export class Randomizer {
    mSeed = 161803398;

    inext: number;
    inextp: number;
    seedArray: number[] = [];

    constructor(baseSeed?: number) {
        let ii: number;
        let mj: number;
        let mk: number;

        const seed = baseSeed || getRandomInt();

        // Initialize our Seed array.
        // This algorithm comes from Numerical Recipes in C (2nd Ed.)
        const subtraction = seed === MIN_VALUE ? MAX_VALUE : Math.abs(seed);
        mj = this.mSeed - subtraction;
        this.seedArray[55] = mj;
        mk = 1;
        for (let i = 1; i < 55; i++) {
            // Apparently the range [1..55] is special (Knuth) and so we're wasting the 0'th position.
            ii = (21 * i) % 55;
            this.seedArray[ii] = mk;
            mk = mj - mk;
            if (mk < 0) mk += MAX_VALUE;
            mj = this.seedArray[ii];
        }
        for (let k = 1; k < 5; k++) {
            for (let i = 1; i < 56; i++) {
                this.seedArray[i] -= this.seedArray[1 + ((i + 30) % 55)];
                if (this.seedArray[i] < 0) this.seedArray[i] += MAX_VALUE;
            }
        }
        this.inext = 0;
        this.inextp = 21;
    }

    sample() {
        return this.internalSample() * (1.0 / MAX_VALUE);
    }

    internalSample() {
        let retVal: number;
        let locINext = this.inext;
        let locINextp = this.inextp;

        locINext += 1;
        if (locINext >= 56) locINext = 1;
        locINextp += 1;
        if (locINextp >= 56) locINextp = 1;

        retVal = this.seedArray[locINext] - this.seedArray[locINextp];

        if (retVal === MAX_VALUE) retVal -= 1;
        if (retVal < 0) retVal += MAX_VALUE;

        this.seedArray[locINext] = retVal;

        this.inext = locINext;
        this.inextp = locINextp;

        return retVal;
    }

    getSampleForLargeRange() {
        // The distribution of double value returned by Sample
        // is not distributed well enough for a large range.
        // If we use Sample for a range [Int32.MinValue..Int32.MaxValue)
        // We will end up getting even numbers only.

        let result = this.internalSample();
        // Note we can't use addition here. The distribution will be bad if we do that.
        let negative = this.internalSample() % 2 == 0; // decide the sign based on second sample
        if (negative) {
            result = -result;
        }
        let d = result;
        d += MAX_VALUE - 1; // get a number in range [0 .. 2 * Int32MaxValue - 1)
        d /= 2 * MAX_VALUE - 1;
        return d;
    }

    next(props?: Next): number {
        let { minValue = 0, maxValue } = props || {};

        // .Net version of Next() uses int for min & max, so we truncate them to keep only the integer part
        minValue = Math.trunc(minValue);
        if (maxValue) {
            maxValue = Math.trunc(maxValue);
        }

        if (maxValue != null && maxValue >= minValue) {
            const range = maxValue - minValue;
            if (range <= MAX_VALUE) {
                return Math.trunc(this.sample() * range) + minValue;
            }
            return Math.trunc(this.getSampleForLargeRange() * range + minValue);
        }
        return this.internalSample();
    }
}
